import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export function ssnSubjectMismatchValidator(recordsFormArray: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const subjectToSSNMap: { [subject: string]: { employmentSS: string, irsSS: string, socialSecuritySS: string }[] } = {};
        const errors: ValidationErrors = { ssnSubjectMismatch: [] };
        for (const record of recordsFormArray.controls) {
            const subject = record.get('subject')?.value;
            const employmentSSControl = record.get('employment.employmentSS');
            const irsSSControl = record.get('irs.irsSS');
            const socialSecuritySSControl = record.get('socialSecurity.socialSecuritySS');

            const selectedTypesControl = record.get('selectedTypes')?.value;
            const employmentSS = selectedTypesControl?.includes('employment') ? employmentSSControl.value : null;
            const irsSS = selectedTypesControl?.includes('irs') ? irsSSControl.value : null;
            const socialSecuritySS = selectedTypesControl?.includes('social-security') ? socialSecuritySSControl.value : null;

            const ssnValues: any = { employmentSS, irsSS, socialSecuritySS };
            const enabledSSNs = Object.keys(ssnValues).filter(key => ssnValues[key] !== null);

            if (enabledSSNs.length > 1) {
                const firstEnabledSSN = ssnValues[enabledSSNs[0]];

                for (let i = 1; i < enabledSSNs.length; i++) {
                    const currentSSN = ssnValues[enabledSSNs[i]];

                    if (currentSSN !== firstEnabledSSN) {
                        if (!errors["ssnSubjectMismatch"].includes(subject)) {
                            errors["ssnSubjectMismatch"].push(subject);
                        }
                        break;
                    }
                }
            }

            if (subject) {
                if (!subjectToSSNMap[subject]) {
                    subjectToSSNMap[subject] = [];
                }
                subjectToSSNMap[subject].push({ employmentSS, irsSS, socialSecuritySS });
            }
        }

        for (const subject in subjectToSSNMap) {
            const ssns = subjectToSSNMap[subject];
            const referenceSSNs: any = ssns[0];

            for (const ssnSet of ssns) {
                const { employmentSS, irsSS, socialSecuritySS } = ssnSet;
                const ssnValues: any = { employmentSS, irsSS, socialSecuritySS };
                const enabledSSNs = Object.keys(ssnValues).filter(key => ssnValues[key] !== null);
                const refValue = Object.keys(referenceSSNs).filter(key => referenceSSNs[key] !== null)[0];

                for (const enabledSSN of enabledSSNs) {
                    if (referenceSSNs[refValue] && ssnValues[enabledSSN] !== referenceSSNs[refValue]) {
                        if (!errors["ssnSubjectMismatch"].includes(subject)) {
                            errors["ssnSubjectMismatch"].push(subject);
                        }
                        break;
                    }
                }
            }
        }

        return errors["ssnSubjectMismatch"].length > 0 ? { ssnSubjectMismatch: errors["ssnSubjectMismatch"] } : null;
    };
}


export function addProviderSsnSubjectValidator(recordsFormArray: any, tableData: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const subjectToSSNMap: { [subject: string]: { employmentSS: string, irsSS: string, socialSecuritySS: string }[] } = {};
    const errors: ValidationErrors = { ssnSubjectMismatch: [] };
    const records = recordsFormArray.controls;

    for (const record of records) {
      const subject = record.get('subject')?.value;

      // Find matching object from tableData
      const originalRecord = tableData.find((d: any) => d.recordSubject === subject);
      const details = originalRecord?.additionalInfo?.details || {};

      // Expected values from table data
      const employmentSSExpected = details?.employment?.ssn || null;
      const irsSSExpected = details?.irs?.ssn || null;
      const socialSecuritySSExpected = details?.socialSecurity?.ssn || null;

      // Values from form controls
      const employmentSSControl = record.get('employment.employmentSS');
      const irsSSControl = record.get('irs.irsSS');
      const socialSecuritySSControl = record.get('socialSecurity.socialSecuritySS');
      const selectedTypesControl = record.get('selectedTypes')?.value;

      const employmentSS = selectedTypesControl?.includes('employment') ? employmentSSControl?.value : null;
      const irsSS = selectedTypesControl?.includes('irs') ? irsSSControl?.value : null;
      const socialSecuritySS = selectedTypesControl?.includes('social-security') ? socialSecuritySSControl?.value : null;

      const ssnValues: any = { employmentSS, irsSS, socialSecuritySS };
      const enabledSSNs = Object.keys(ssnValues).filter(key => ssnValues[key] !== null);

      // 1. Compare within form values (e.g. employment vs IRS vs SSN)
      if (enabledSSNs.length > 1) {
        const firstSSN = ssnValues[enabledSSNs[0]];
        for (let i = 1; i < enabledSSNs.length; i++) {
          const currentSSN = ssnValues[enabledSSNs[i]];
          if (currentSSN !== firstSSN) {
            if (!errors["ssnSubjectMismatch"].includes(subject)) {
              errors["ssnSubjectMismatch"].push(subject);
            }
            break;
          }
        }
      }

      // 2. Compare form values with expected values from original data
      const mismatchedFields: string[] = [];
      if (employmentSS && employmentSSExpected && employmentSS !== employmentSSExpected) {
        mismatchedFields.push('employmentSS');
      }
      if (irsSS && irsSSExpected && irsSS !== irsSSExpected) {
        mismatchedFields.push('irsSS');
      }
      if (socialSecuritySS && socialSecuritySSExpected && socialSecuritySS !== socialSecuritySSExpected) {
        mismatchedFields.push('socialSecuritySS');
      }

      if (mismatchedFields.length > 0) {
        if (!errors["ssnSubjectMismatch"].includes(subject)) {
          errors["ssnSubjectMismatch"].push(subject);
        }
      }

      // Track all SSNs per subject for cross-subject validation
      if (subject) {
        if (!subjectToSSNMap[subject]) {
          subjectToSSNMap[subject] = [];
        }
        subjectToSSNMap[subject].push({ employmentSS, irsSS, socialSecuritySS });
      }
    }

    // 3. Compare SSNs across records for same subject (multi-entry validation)
    for (const subject in subjectToSSNMap) {
      const ssns = subjectToSSNMap[subject];
      const referenceSSNs = ssns[0];

      for (const ssnSet of ssns) {
        const ssnValues = { ...ssnSet };
        type SSNKey = keyof typeof ssnValues;
        const enabledSSNs = (Object.keys(ssnValues) as SSNKey[]).filter(key => ssnValues[key] !== null);
        
        const refValueKey = (Object.keys(referenceSSNs) as SSNKey[]).find(key => referenceSSNs[key] !== null);

        for (const enabledSSN of enabledSSNs) {
          if (refValueKey && referenceSSNs[refValueKey] && ssnValues[enabledSSN] !== referenceSSNs[refValueKey]) {
            if (!errors["ssnSubjectMismatch"].includes(subject)) {
              errors["ssnSubjectMismatch"].push(subject);
            }
            break;
          }
        }
      }
    }

    return errors["ssnSubjectMismatch"].length > 0 ? errors : null;
  };
}



